import { GridToolbar } from '@mui/x-data-grid';
import type { DataGridProps, GridPaginationModel } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';

/**
 * Common DataGrid styling configuration for consistent appearance across the application
 */
export const commonDataGridSx = {
    height: '100%',
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
        color: 'secondary.main'
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: 'secondary.main'
    },
    '& .MuiDataGrid-columnSeparator': {
        color: 'secondary.main',
    },
    '& .MuiDataGrid-sortIcon': {
        color: 'secondary.main',
    },
    '& .MuiDataGrid-menuIcon': {
        color: 'secondary.main',
    },
    '& .MuiDataGrid-toolbarContainer': {
        '& .MuiButton-root': {
            color: 'secondary.main',
        },
        '& .MuiInputBase-root': {
            color: 'secondary.main',
            fontWeight: 'bold'
        },
        '& .MuiSvgIcon-root': {
            color: 'secondary.main',
        },
    },
    '& .MuiTablePagination-root': {
        color: 'secondary.main',
        '& .MuiTablePagination-selectIcon': {
            color: 'secondary.main',
        },
        '& .MuiTablePagination-actions button': {
            color: 'secondary.main',
        },
    },
};

/**
 * Common DataGrid configuration for consistent styling and behavior across the application
 */
export const commonDataGridConfig: Partial<DataGridProps> = {
    // Slots configuration
    slots: {
        toolbar: GridToolbar,
    },
    
    // Slot props configuration
    slotProps: {
        toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
        },
    },

    // Common styling configuration
    sx: commonDataGridSx,

    // Common behavior configuration
    density: "compact",
    disableRowSelectionOnClick: true,
};

/**
 * Merges common DataGrid config with custom overrides
 * @param customConfig Custom configuration to override defaults
 * @returns Merged configuration object
 * 
 * @example
 * // Basic usage - apply all common styles
 * <DataGrid 
 *   rows={data} 
 *   columns={columns} 
 *   {...mergeDataGridConfig()} 
 * />
 * 
 * @example  
 * // With custom overrides
 * <DataGrid 
 *   rows={data}
 *   columns={columns}
 *   {...mergeDataGridConfig({
 *     sx: { minHeight: '500px' },
 *     pageSizeOptions: [10, 20, 50]
 *   })}
 * />
 */
export const mergeDataGridConfig = (customConfig: Partial<DataGridProps> = {}): Partial<DataGridProps> => {
    return {
        ...commonDataGridConfig,
        ...customConfig,
        sx: {
            ...commonDataGridSx,
            ...customConfig.sx,
        },
        slotProps: {
            ...commonDataGridConfig.slotProps,
            ...customConfig.slotProps,
        },
        slots: {
            ...commonDataGridConfig.slots,
            ...customConfig.slots,
        },
    };
};

/**
 * Hook that manages DataGrid pagination so the page containing a selected row
 * is automatically shown. The user can still freely navigate pages afterwards.
 *
 * @param rows       The full row array passed to the DataGrid (must have a `rowNumber` field)
 * @param selectedRowId  The rowNumber to scroll/page to, or null for no selection
 * @param pageSize   Initial page size (default 10)
 * @returns [paginationModel, setPaginationModel] — spread paginationModel into the DataGrid
 */
export const useDataGridPageForSelection = (
    rows: Array<{ rowNumber: number }>,
    selectedRowId: number | null | undefined,
    pageSize = 10
): [GridPaginationModel, React.Dispatch<React.SetStateAction<GridPaginationModel>>] => {
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize });

    const pageSizeRef = useState(pageSize)[0];
    const paginationModelRef = { current: paginationModel };
    paginationModelRef.current = paginationModel;

    useEffect(() => {
        if (selectedRowId == null) return;
        const index = rows.findIndex(r => r.rowNumber === selectedRowId);
        if (index < 0) return;
        const targetPage = Math.floor(index / paginationModelRef.current.pageSize);
        setPaginationModel(prev => ({ ...prev, page: targetPage }));
    }, [selectedRowId]); // intentionally omit paginationModel — only jump when selection changes

    return [paginationModel, setPaginationModel];
};